import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Badge, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ApiService from '../../helpers/apiService';
import { hasAccess } from '../../helpers/access';

// Задачи/бэклог — GitLab Work Items проекта common (CLAUDE.md §3.6), фильтр по labels.
// admin-api проксирует issues (GET /tasks/v1/issues); токен — на стороне admin-api.

interface GLIssue {
  iid: number;
  title: string;
  state: string;
  web_url: string;
  updated_at: string;
  labels: string[];
  assignees: string[];
}

// Команды для маршрутизации по label team:: (перевод задачи на другую команду). См. labels.md.
const TEAMS = [
  'cards', 'users', 'accounts', 'scoring', 'kyc', 'limiter', 'sender', 'crypto-gateway',
  'admin-api', 'platform', 'mobile', 'devops', 'wiki-docs',
];

// Цвет бейджа по префиксу label (prio::/type::/team::/doc-backlog). См. labels.md.
const labelVariant = (l: string): string => {
  if (l.startsWith('prio::P0')) return 'danger';
  if (l.startsWith('prio::P1')) return 'warning';
  if (l.startsWith('prio::')) return 'secondary';
  if (l.startsWith('team::')) return 'info';
  if (l.startsWith('type::security') || l.startsWith('type::compliance')) return 'danger';
  if (l.startsWith('type::')) return 'light';
  if (l.startsWith('status::')) return 'warning';
  if (l === 'doc-backlog') return 'dark';
  return 'light';
};

const GitlabTasksTab: React.FC = () => {
  const [labels, setLabels] = useState('doc-backlog');
  const [state, setState] = useState('opened');
  const [items, setItems] = useState<GLIssue[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<Array<{ id: number; name: string; username: string }>>([]);
  const [busyIid, setBusyIid] = useState<number | null>(null);

  const canEdit = hasAccess('backlogEdit');

  const load = useCallback(() => {
    setLoading(true);
    setMessage('');
    ApiService.gitlabTasksGet(labels.trim() || 'doc-backlog', state)
      .then(({ items: it, message: m }) => {
        setItems(it as GLIssue[]);
        if (m) setMessage(m);
      })
      .finally(() => setLoading(false));
  }, [labels, state]);

  useEffect(() => {
    load();
    if (canEdit) {
      ApiService.gitlabMembersGet().then(setMembers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Изменение задачи (close/reopen/reassign) → перезагрузка списка.
  const update = useCallback(
    (iid: number, body: { state_event?: string; assignee_id?: number; add_labels?: string; remove_labels?: string }) => {
      setBusyIid(iid);
      ApiService.gitlabTaskUpdate(iid, body)
        .then(() => load())
        .catch(err => alert('Не удалось изменить задачу: ' + (err?.response?.data?.message || err?.message || 'ошибка')))
        .finally(() => setBusyIid(null));
    },
    [load]
  );

  return (
    <Card>
      <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <Card.Title className="mb-0">
          <FontAwesomeIcon icon="tasks" className="me-2 text-primary" />
          Бэклог (GitLab Work Items · common)
        </Card.Title>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Form.Control
            size="sm"
            style={{ width: 220 }}
            value={labels}
            onChange={e => setLabels(e.target.value)}
            placeholder="labels (через запятую)"
            aria-label="Фильтр по labels"
          />
          <Form.Select size="sm" style={{ width: 130 }} value={state} onChange={e => setState(e.target.value)} aria-label="Статус">
            <option value="opened">Открытые</option>
            <option value="closed">Закрытые</option>
            <option value="all">Все</option>
          </Form.Select>
          <Button size="sm" variant="outline-primary" onClick={load} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon="sync" />}
          </Button>
          <a
            href="https://gitlab.com/gsmsoft1/exchange/common/-/work_items"
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm btn-outline-secondary"
          >
            Открыть в GitLab
          </a>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {loading && (
          <div className="text-center py-4 text-600">
            <Spinner animation="border" size="sm" className="me-2" /> Загрузка…
          </div>
        )}
        {!loading && message && items.length === 0 && (
          <Alert variant="warning" className="m-3 mb-0">
            {message}
            <div className="small text-muted mt-1">
              Источник задач — GitLab Work Items проекта <code>common</code>, фильтр по labels. Токен настраивается
              в admin-api (<code>GITLAB_API_TOKEN</code>).
            </div>
          </Alert>
        )}
        {!loading && items.length > 0 && (
          <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>Задача</th>
                <th>Labels</th>
                <th>Исполнители</th>
                <th style={{ width: 90 }}>Статус</th>
                <th style={{ width: 120 }}>Обновлено</th>
                {canEdit && <th style={{ width: 230 }}>Действия</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.iid}>
                  <td className="text-600">#{it.iid}</td>
                  <td>
                    <a href={it.web_url} target="_blank" rel="noreferrer">
                      {it.title}
                    </a>
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {(it.labels || []).map(l => (
                        <Badge key={l} bg={labelVariant(l)} text={labelVariant(l) === 'light' ? 'dark' : undefined} style={{ fontSize: '0.66rem' }}>
                          {l}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="small">{(it.assignees || []).join(', ') || '—'}</td>
                  <td>
                    <Badge bg={it.state === 'opened' ? 'success' : 'secondary'} style={{ fontSize: '0.7rem' }}>
                      {it.state === 'opened' ? 'Открыта' : 'Закрыта'}
                    </Badge>
                  </td>
                  <td className="small text-600">
                    {it.updated_at ? new Date(it.updated_at).toLocaleDateString() : '—'}
                  </td>
                  {canEdit && (
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        {it.state === 'opened' ? (
                          <Button
                            size="sm"
                            variant="outline-success"
                            style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                            disabled={busyIid === it.iid}
                            onClick={() => update(it.iid, { state_event: 'close' })}
                            title="Закрыть задачу"
                          >
                            {busyIid === it.iid ? <Spinner animation="border" size="sm" /> : 'Закрыть'}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                            disabled={busyIid === it.iid}
                            onClick={() => update(it.iid, { state_event: 'reopen' })}
                            title="Переоткрыть задачу"
                          >
                            Открыть
                          </Button>
                        )}
                        {/* Перевод на команду через label team:: (маршрутизация по labels). */}
                        <Form.Select
                          size="sm"
                          style={{ fontSize: '0.7rem', maxWidth: 120 }}
                          value=""
                          disabled={busyIid === it.iid}
                          onChange={e => {
                            const v = e.target.value;
                            if (!v) return;
                            const cur = (it.labels || []).filter(l => l.startsWith('team::'));
                            update(it.iid, { add_labels: `team::${v}`, remove_labels: cur.join(',') });
                          }}
                          aria-label="Перевести на команду"
                          title="Перевести на команду (label team::)"
                        >
                          <option value="">Команда…</option>
                          {TEAMS.map(t => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </Form.Select>
                        {/* Назначение на человека — если admin-api токен имеет доступ к members. */}
                        {members.length > 0 && (
                          <Form.Select
                            size="sm"
                            style={{ fontSize: '0.7rem', maxWidth: 130 }}
                            value=""
                            disabled={busyIid === it.iid}
                            onChange={e => {
                              const v = e.target.value;
                              if (v !== '') update(it.iid, { assignee_id: Number(v) });
                            }}
                            aria-label="Переназначить исполнителя"
                            title="Переназначить исполнителя"
                          >
                            <option value="">Исполнитель…</option>
                            <option value="0">— Снять —</option>
                            {members.map(m => (
                              <option key={m.id} value={m.id}>
                                {m.name}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {!loading && !message && items.length === 0 && (
          <div className="text-center text-600 py-4">Задач по фильтру не найдено.</div>
        )}
      </Card.Body>
    </Card>
  );
};

export default GitlabTasksTab;
